import React from "react";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, AlertTriangle, Cloud, CloudOff, ArrowUpRight } from "lucide-react";
import { adminApi } from "../lib/api";

/**
 * BillyPanel — admin-facing real-time status + manual backfill for the
 * Billy.dk accounting integration.
 *
 * Shows:
 *  - Connection card (org name, country, currency, auto-push)
 *  - Pending invoice counters (bookings + monthly)
 *  - Backfill controls: dry-run + live with limit selector
 *  - Last sync timestamp (persisted in localStorage)
 *  - Per-item result table after a backfill run
 */
export default function BillyPanel() {
  const [status, setStatus] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [limit, setLimit] = React.useState(50);
  const [results, setResults] = React.useState(null);
  const [lastSyncAt, setLastSyncAt] = React.useState(localStorage.getItem("billy_last_sync_at") || null);

  const loadStatus = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get("/admin/billy/status");
      setStatus(data);
    } catch (e) {
      toast.error("Kunne ikke hente Billy-status");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { loadStatus(); }, [loadStatus]);

  const runBackfill = async (dryRun) => {
    setBusy(true);
    setResults(null);
    try {
      const { data } = await adminApi().post("/admin/billy/backfill", { limit, dry_run: dryRun });
      setResults(data);
      if (!dryRun) {
        const ts = new Date().toISOString();
        localStorage.setItem("billy_last_sync_at", ts);
        setLastSyncAt(ts);
        toast.success(`Sync færdig: ${data?.summary?.bookings_synced ?? 0} booking(s), ${data?.summary?.monthly_synced ?? 0} månedsfaktura(er)`);
      } else {
        toast.success("Dry-run færdig");
      }
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill fejlede");
    } finally {
      setBusy(false);
    }
  };

  if (loading && !status) {
    return <div className="text-brand-text2" data-testid="billy-loading">Indlæser Billy-status...</div>;
  }

  const conn = status?.connection || {};
  const enabled = !!status?.enabled && !!conn.ok;
  const totalPending = (status?.pending_booking_invoices || 0) + (status?.pending_monthly_invoices || 0);

  return (
    <div className="space-y-6" data-testid="billy-panel">
      {/* Connection + pending summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className={`p-5 border ${enabled ? "border-emerald-300 bg-emerald-50/40" : "border-amber-300 bg-amber-50/40"}`} data-testid="billy-connection-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-brand-dark font-semibold">
              {enabled ? <Cloud size={18} className="text-emerald-700" /> : <CloudOff size={18} className="text-amber-700" />}
              Billy-forbindelse
            </div>
            {enabled
              ? <CheckCircle2 size={18} className="text-emerald-700" />
              : <AlertTriangle size={18} className="text-amber-700" />}
          </div>
          {enabled ? (
            <div className="mt-3 text-sm space-y-1">
              <div><span className="text-brand-text2">Org:</span> <span className="font-semibold text-brand-dark" data-testid="billy-org-name">{conn.name || "—"}</span></div>
              <div className="text-brand-text2">{conn.country || "—"} · {conn.currency || "—"}</div>
              <div className="text-brand-text2">Auto-push: <span className={conn.auto_push ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"}>{conn.auto_push ? "Til" : "Fra"}</span></div>
              <div className="text-brand-text2">Momssats: {conn.has_vat_tax_rate ? <span className="text-emerald-700 font-semibold">Konfigureret</span> : <span className="text-amber-700 font-semibold">Mangler</span>}</div>
            </div>
          ) : (
            <div className="mt-3 text-sm text-brand-text2">
              <p>{conn.reason || "Billy ikke konfigureret"}</p>
              <p className="mt-2 text-xs">Sæt <code>BILLY_API_TOKEN</code> + <code>BILLY_ORGANIZATION_ID</code> i backend/.env.</p>
            </div>
          )}
        </div>

        <div className="p-5 border border-brand bg-white" data-testid="billy-pending-bookings-card">
          <div className="text-xs uppercase tracking-widest text-brand-text2">Ventende bookings</div>
          <div className="font-display font-bold text-4xl text-brand-dark mt-2" data-testid="billy-pending-bookings">
            {status?.pending_booking_invoices ?? "—"}
          </div>
          <div className="text-xs text-brand-text2 mt-1">betalte bookings uden faktura i Billy</div>
        </div>

        <div className="p-5 border border-brand bg-white" data-testid="billy-pending-monthly-card">
          <div className="text-xs uppercase tracking-widest text-brand-text2">Ventende månedsfakturaer</div>
          <div className="font-display font-bold text-4xl text-brand-dark mt-2" data-testid="billy-pending-monthly">
            {status?.pending_monthly_invoices ?? "—"}
          </div>
          <div className="text-xs text-brand-text2 mt-1">B2B-fakturaer der mangler eksport</div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-brand-dark text-white p-6 border border-brand-dark" data-testid="billy-actions">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="font-display font-bold text-xl">Manuel sync</div>
            <div className="text-white/70 text-sm mt-1">
              Push ventende fakturaer til Billy nu. Auto-push kører også automatisk når Stripe bekræfter betaling.
            </div>
            {lastSyncAt && (
              <div className="text-xs text-white/60 mt-2" data-testid="billy-last-sync">
                Sidst kørt: {new Date(lastSyncAt).toLocaleString("da-DK")}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-white/70 mb-1">Limit</label>
            <input
              type="number" min={1} max={200} value={limit}
              onChange={(e) => setLimit(Math.max(1, Math.min(200, parseInt(e.target.value || "1", 10))))}
              className="w-24 px-3 py-2 bg-white/10 border border-white/20 text-white"
              data-testid="billy-limit-input"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => runBackfill(true)}
              disabled={busy || !enabled}
              className="px-5 py-2.5 bg-white/10 hover:bg-white/15 text-white text-sm font-semibold border border-white/20 disabled:opacity-50 transition"
              data-testid="billy-dryrun-btn"
            >
              Dry-run
            </button>
            <button
              onClick={() => runBackfill(false)}
              disabled={busy || !enabled || totalPending === 0}
              className="px-5 py-2.5 bg-brand-red hover:bg-brand-red/90 text-white text-sm font-semibold border border-brand-red disabled:opacity-50 transition inline-flex items-center gap-2"
              data-testid="billy-sync-btn"
            >
              {busy ? <RefreshCw size={14} className="animate-spin" /> : <ArrowUpRight size={14} />}
              {busy ? "Synker..." : `Sync nu (${totalPending})`}
            </button>
            <button
              onClick={loadStatus}
              disabled={busy}
              className="px-3 py-2.5 bg-white/10 hover:bg-white/15 text-white border border-white/20 disabled:opacity-50"
              title="Genindlæs status"
              data-testid="billy-refresh-btn"
            >
              <RefreshCw size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      {results && (
        <div className="bg-white border border-brand p-5" data-testid="billy-results">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-brand-dark">
              {results.dry_run ? "Dry-run resultat" : "Sync resultat"}
            </div>
            <div className="text-xs text-brand-text2">
              Bookings synket: {results.summary?.bookings_synced ?? 0} · fejl: {results.summary?.bookings_failed ?? 0} ·
              Månedsfakturaer synket: {results.summary?.monthly_synced ?? 0} · fejl: {results.summary?.monthly_failed ?? 0}
            </div>
          </div>

          {results.results?.bookings?.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <div className="text-xs uppercase tracking-widest text-brand-text2 mb-2">Bookings</div>
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-brand-text2 uppercase tracking-widest border-b border-brand">
                  <tr><th className="py-2">Booking</th><th>Total</th><th>Status</th><th>Billy ID / Fejl</th></tr>
                </thead>
                <tbody>
                  {results.results.bookings.map((r) => (
                    <tr key={r.id || r.booking_number} className="border-b border-slate-100">
                      <td className="py-2 font-mono text-xs">{r.booking_number}</td>
                      <td>{r.total_dkk?.toLocaleString("da-DK")} kr</td>
                      <td>
                        <span className={`pill-status ${
                          r.status === "synced" ? "bg-emerald-100 text-emerald-900"
                          : r.status === "would_push" ? "bg-blue-100 text-blue-900"
                          : "bg-red-100 text-red-900"}`}>{r.status}</span>
                      </td>
                      <td className="font-mono text-xs text-brand-text2">{r.billy_invoice_id || r.error || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {results.results?.monthly_invoices?.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <div className="text-xs uppercase tracking-widest text-brand-text2 mb-2">Månedsfakturaer</div>
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-brand-text2 uppercase tracking-widest border-b border-brand">
                  <tr><th className="py-2">Faktura</th><th>Total</th><th>Status</th><th>Billy ID / Fejl</th></tr>
                </thead>
                <tbody>
                  {results.results.monthly_invoices.map((r) => (
                    <tr key={r.invoice_number} className="border-b border-slate-100">
                      <td className="py-2 font-mono text-xs">{r.invoice_number}</td>
                      <td>{r.total_dkk?.toLocaleString("da-DK")} kr</td>
                      <td>
                        <span className={`pill-status ${
                          r.status === "synced" ? "bg-emerald-100 text-emerald-900"
                          : r.status === "would_push" ? "bg-blue-100 text-blue-900"
                          : "bg-red-100 text-red-900"}`}>{r.status}</span>
                      </td>
                      <td className="font-mono text-xs text-brand-text2">{r.billy_invoice_id || r.error || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(!results.results?.bookings?.length && !results.results?.monthly_invoices?.length) && (
            <div className="mt-3 text-sm text-brand-text2">Ingen fakturaer at pushe — alt er synket.</div>
          )}
        </div>
      )}

      {/* Security reminder */}
      <div className="bg-amber-50 border border-amber-300 p-4 text-sm text-amber-900" data-testid="billy-security-note">
        <strong>🔐 Tip:</strong> Rotér din Billy API-token periodisk. Gå til Billy → Indstillinger → API → "Forny token" og opdater <code>BILLY_API_TOKEN</code> i backend/.env.
      </div>
    </div>
  );
}
